/**
 * use-platform-pricing.ts — Hooks cho Platform → Bảng giá gói (super-admin).
 * Gọi qua `platformApi` (token platform riêng), giống các hook trong use-platform.ts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from '@/lib/platform-client'
import type { PricingConfig, PricingResponse, Quote, Quotas } from '@/pages/platform/pricing-editor'

const KEY = ['platform', 'billing', 'pricing'] as const

export function usePlatformPricing() {
  return useQuery<PricingResponse>({
    queryKey: KEY,
    queryFn: async () => {
      const { data } = await platformApi.get('/platform/billing/pricing')
      return data
    },
    staleTime: 60_000,
  })
}

export function useSavePricing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (pricing: PricingConfig) => {
      const { data } = await platformApi.put<PricingResponse>('/platform/billing/pricing', { pricing })
      return data
    },
    onSuccess: (data) => qc.setQueryData<PricingResponse>(KEY, (old) => ({ ...old, ...data })),
  })
}

export function useResetPricing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await platformApi.delete<PricingResponse>('/platform/billing/pricing')
      return data
    },
    onSuccess: (data) => qc.setQueryData<PricingResponse>(KEY, (old) => ({ ...old, ...data })),
  })
}

export interface PricingPreviewInput {
  pricing: PricingConfig
  planId: string
  days: number
  quantities: Quotas
}

/** Báo giá thử với bảng giá NHÁP (chưa lưu). Lỗi 400 mang `{ error }` tiếng Việt. */
export async function previewPricing(input: PricingPreviewInput, signal?: AbortSignal): Promise<Quote> {
  const { data } = await platformApi.post<Quote>('/platform/billing/pricing/preview', input, { signal })
  return data
}
